import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView } from './ChartView'
import { formatNumber } from './chartFormat'

/**
 * The state crime lookup chart: any state's violent or property crime rate each
 * year since 1985, drawn next to the U.S. rate (a dashed line). The page's own
 * data (`chart.data`) is the featured state's violent crime rate; `chart.state`
 * is the state in the address (`?state=texas`) when the server already has it;
 * the U.S. rates are `chart.measures` (Chart#lookup_data); any other state comes
 * from GET /state-crime/:slug.json (StateCrimesController). The address follows
 * each choice, so a state's page can be shared and found.
 */

const FEATURED = { name: 'California', slug: 'california' } // keep in step with chart.yml
const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
].map((name) => ({ name, slug: name.toLowerCase().replace(/\s+/g, '-') })) // StateProfile.slug_for
const MEASURES = {
  violent: { button: 'Violent crime', yLabel: 'Violent crimes per 100,000 people', words: 'violent crimes' },
  property: { button: 'Property crime', yLabel: 'Property crimes per 100,000 people', words: 'property crimes' },
}

function setAddress(slug, measure) {
  const url = new URL(window.location.href)
  url.searchParams.set('state', slug)
  if (measure === 'violent') url.searchParams.delete('measure')
  else url.searchParams.set('measure', measure)
  window.history.replaceState(null, '', url)
}

/**
 * Every year from the first to the last of either series, with the state's
 * rate or null where it has none, so a gap breaks the line instead of joining
 * the years either side of it.
 */
export function everyYear(points, usPoints) {
  const years = [...points, ...usPoints].map((p) => Number(p.label)).filter(Number.isInteger)
  if (!years.length) return points
  const rates = new Map(points.map((p) => [String(p.label), p.value]))
  const rows = []
  for (let year = Math.min(...years); year <= Math.max(...years); year += 1) {
    rows.push({ label: String(year), value: rates.get(String(year)) ?? null })
  }
  return rows
}

/** "1995–2009 and 2021" for the years with no rate. */
export function yearSpans(rows) {
  const spans = []
  rows.forEach((row) => {
    if (row.value != null) return
    const last = spans[spans.length - 1]
    if (last && Number(last[1]) + 1 === Number(row.label)) last[1] = row.label
    else spans.push([row.label, row.label])
  })
  const words = spans.map(([from, to]) => (from === to ? from : `${from}–${to}`))
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0] ?? ''
}

export function StateCrimeLookup({ chart }) {
  const selectId = useId()
  const [result, setResult] = useState(chart.state ?? { ...FEATURED, measures: { violent: chart.data } })
  const [measure, setMeasure] = useState(() => {
    const asked = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('measure')
    return MEASURES[asked] ? asked : 'violent'
  })
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)
  const us = chart.measures ?? {}

  const lookUp = async (slug, chosen = measure) => {
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const response = await fetch(`/state-crime/${encodeURIComponent(slug)}.json`, { headers: { Accept: 'application/json' }, signal: controller.signal })
      if (!response.ok) throw new Error(`Request failed (${response.status})`)
      setResult(await response.json())
      setMeasure(chosen)
      setStatus({ loading: false, message: null })
      setAddress(slug, chosen)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That state’s rates couldn’t be loaded from the FBI. Please try again.' })
    }
  }

  // Open the state in the address, unless the server already sent it.
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get('state')
    if (asked && asked !== result.slug && STATES.some((s) => s.slug === asked)) lookUp(asked)
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chooseMeasure = (value) => {
    if (!result.measures[value]) {
      lookUp(result.slug, value) // the featured state arrives with its violent crime rate only
      return
    }
    setMeasure(value)
    setAddress(result.slug, value)
  }

  const meta = MEASURES[measure]
  const points = result.measures[measure] ?? []
  const usPoints = us[measure] ?? []
  const latest = points[points.length - 1]
  const usLatest = latest ? usPoints.find((p) => p.label === latest.label) : null
  const usByYear = new Map(usPoints.map((p) => [p.label, p.value]))
  const rows = everyYear(points, usPoints)
  const missing = yearSpans(rows)

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 sm:w-72">
          <label htmlFor={selectId} className="form-label">Choose a state</label>
          <select
            id={selectId}
            className="input-form-field min-h-10"
            value={result.slug}
            disabled={status.loading}
            onChange={(event) => lookUp(event.target.value)}
          >
            {STATES.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Measure">
          {Object.entries(MEASURES).map(([value, { button }]) => (
            <button
              key={value}
              type="button"
              className={`${measure === value ? 'btn-primary' : 'btn-secondary'} btn-sm min-h-10`}
              aria-pressed={measure === value}
              disabled={status.loading}
              onClick={() => chooseMeasure(value)}
            >
              {button}
            </button>
          ))}
        </div>
      </div>

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      <p className="mb-4 font-semibold text-ink break-words" aria-live="polite">
        {status.loading ? 'Loading…' : result.name}
        {latest ? (
          <span className="block text-sm font-normal text-ink-muted">
            {latest.label}: {formatNumber(latest.value)} {meta.words} per 100,000 people
            {usLatest ? ` (U.S.: ${formatNumber(usLatest.value)})` : ''}.
          </span>
        ) : null}
      </p>

      <ChartView
        searchable={false}
        chart={{
          ...chart,
          title: `${meta.button} rate in ${result.name} and the U.S., each year`,
          y_label: meta.yLabel,
          data: rows,
          series_name: result.name,
          reference: usPoints.length ? { name: 'United States', data: usPoints } : null,
        }}
      />
      {missing ? (
        <p className="mt-2 text-xs text-ink-muted">
          No figure for {result.name} in {missing}: police departments covering fewer than 3 in 4 of its people reported
          a full year to the FBI, so a rate would stand for only part of the state. The line breaks there.
        </p>
      ) : null}

      {points.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-primary">The numbers for {result.name}</summary>
          <div className="mt-2 max-h-80 overflow-y-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th scope="col" className="text-left">Year</th>
                  <th scope="col" className="text-right">{result.name}</th>
                  <th scope="col" className="text-right">United States</th>
                </tr>
              </thead>
              <tbody>
                {[...rows].reverse().map((p) => (
                  <tr key={p.label}>
                    <td>{p.label}</td>
                    <td className="text-right tabular-nums">{p.value?.toLocaleString('en-US') ?? '—'}</td>
                    <td className="text-right tabular-nums">{usByYear.get(p.label)?.toLocaleString('en-US') ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-ink-muted">{meta.yLabel}, as reported to the FBI.</p>
          </div>
        </details>
      ) : null}
    </div>
  )
}
